import React, { RefObject, useEffect, useMemo, useState } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';
import { ITheadSort, ListLayout } from '../../../../global/tables/table-list-layout';
import TableLoadingRow from '../../../../global/tables/table-loading-row';
import ControlViewModel from '../control.viewmodel';
import { getMeetingGroupsWithHiddenSection, getProgressList, IMeetingsGroup } from '../../../../../infrastructure/utils/common.utils';
import MultiSelectionDropdown from '../../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { SearchInput } from '../../../../global/search-input/search-input';
import { TGetEventsSortKeys } from '../../../../../api/events.dao';
import { Paging } from '../../../../global/_paging/paging-container';
import OENextPrevStepper from '../../../../global/oe-next-prev-stepper/oe-next-prev-stepper';
import { SCROLL_HEADER_HEIGHT } from '../../../../../constants/common.constants';
import ControlListGroupItem from './_control-list-group-item/_control-list-group-item';
import './control-list.scss';
import ControlledMultiSelectionDropdown from '../../../../global/controlled-multi-selection-dropdown/controlled-multi-selection-dropdown';
import { SelectLeftColumnIcon } from '../../../../global/icons/icons';
import { LOAD_SAVED_FILTERS, TRACK_FILTERS } from '../../../../../constants/meeting.constants';
import { EMPTY_SEARCH_RESULT, TYPE } from '../../../../../constants/common-labels.constants';
import DateSortFilterDropdown from '../../../../global/date-sort-filter-dropdown/date-sort-filter-dropdown';

interface IProps {
    controlVM: ControlViewModel;
    intl: IntlShape;
}

const ID_FOR_CONTROL_COLUMN_SELECTOR = uuid();

const ControlList = mobxReact.observer((props: IProps) => {
    const [currentScrollHourSection, setCurrentScrollHourSection] = useState<Date>(new Date());
    const [isScrollNextDisabled, setIsScrollNextDisabled] = useState(false);
    const { controlVM, intl } = props;
    const noOperatorsFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.operator.noOperatorFound'
                    defaultMessage="You don't have any Operators yet."
                    description='label text'
                />
            </div>
            <div>
                <FormattedMessage
                    id='oe.common.createAndProceed'
                    defaultMessage='Please create one and proceed.'
                    description='No operator label text'
                />
            </div>
        </div>
    );


    const noChaperoneFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.organizations.noChaperoneFound'
                    defaultMessage="You don't have any Chaperone yet."
                    description='label text'
                />
            </div>
            <div>
                <FormattedMessage
                    id='oe.common.createAndProceed'
                    defaultMessage='Please create one and proceed.'
                    description='No operator label text'
                />
            </div>
        </div>
    );

    const noMeetingTypesFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.meeting.noTypesFound'
                    defaultMessage="You don't have any meeting with type yet."
                    description='No meeting types label text'
                />
            </div>
        </div>
    );

    const selectedFilterColumnsIds = controlVM.selectedFilterColumns.map(el => el.id);

    const isColumnSelected = (id: string) => {
        return selectedFilterColumnsIds.includes(id);
    };

    const optionalThead = [
        {
            id: 'status',
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage({
                        id: 'oe.common.status',
                        defaultMessage: 'Status',
                        description: 'Label text'
                    })}
                    withProgress={true}
                    withSearch={false}
                    onFetchItems={controlVM.getMeetingStatuses}
                    onApply={statuses => controlVM.handleChangeStatusFilter(statuses)}
                    key={'status'}
                    savedItems={controlVM.getSelectedFilterArray(controlVM.selectedFilterStatuses)}
                    isSetInitialItems={true}
                />
            ),
            width: '70px'
        },
        {
            id: 'presenters',
            title: intl.formatMessage({
                id: 'oe.common.presenters',
                defaultMessage: 'Presenters',
                description: 'Label text'
            })
        },
        {
            id: 'participants',
            title: intl.formatMessage({
                id: 'oe.common.participants',
                defaultMessage: 'Participants',
                description: 'Label text'
            })
        },
        {
            id: 'hosts',
            title: intl.formatMessage({
                id: 'oe.common.hosts',
                defaultMessage: 'Hosts',
                description: 'Label text'
            })
        },
        {
            id: 'type',
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage(TYPE)}
                    noItemsPlaceholder={noMeetingTypesFilterPlaceholder}
                    onApply={types => controlVM.handleChangeTypeFilter(types)}
                    onFetchItems={controlVM.fetchMeetingTypes}
                    key={'type'}
                    savedItems={controlVM.getSelectedFilterArray(controlVM.selectedFilterTypes)}
                    isSetInitialItems={true}
                />
            )
        },
        {
            id: 'operators',
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage({
                        id: 'oe.common.operators',
                        defaultMessage: 'Operators',
                        description: 'Label text'
                    })}
                    noItemsPlaceholder={noOperatorsFilterPlaceholder}
                    onApply={operatorIds => controlVM.handleChangeOperatorsFilter(operatorIds)}
                    onFetchItems={controlVM.fetchOperators}
                    key={'operators'}
                    savedItems={controlVM.getSelectedFilterArray(controlVM.selectedFilterOperators)}
                    isSetInitialItems={true}
                />
            )
        },
        {
            id: 'chaperone',
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage({
                        id: 'oe.common.chaperone',
                        defaultMessage: 'Chaperone',
                        description: 'Label text'
                    })}
                    noItemsPlaceholder={noChaperoneFilterPlaceholder}
                    onApply={chaperone => controlVM.handleChangeChaperoneFilter(chaperone)}
                    onFetchItems={controlVM.fetchChaperone}
                    key={'chaperone'}
                    savedItems={controlVM.getSelectedFilterArray(controlVM.selectedChaperone)}
                    isSetInitialItems={true}
                />
            )
        },
        {
            id: 'connections',
            title: intl.formatMessage({
                id: 'oe.common.connections',
                defaultMessage: 'Connections',
                description: 'Label text'
            })
        },
    ];
    const theads: ITheadSort<TGetEventsSortKeys>[] = [
        {
            id: 'dateTime',
            title: (
                <DateSortFilterDropdown
                    hasSelectedDateRange={controlVM.hasSelectedDateRange()}
                    availableDatesList={controlVM.filterDays}
                    fetchAvailableDates={controlVM.fetchFilterHours}
                    onSelectDates={controlVM.selectDates}
                    firstSelectedDate={controlVM.firstSelectedDate}
                    lastSelectedDate={controlVM.lastSelectedDate}
                    selectedSortOrder={controlVM.selectedSortOrder}
                    selectedFilterDays={controlVM.getFilterDaysInEventTimezone(controlVM.selectedFilterDays)}
                    onReset={controlVM.clearFilteredDays}
                    onApply={controlVM.applyFilters}
                    isMeetingsDatesLoading={controlVM.isMeetingsDatesLoading}
                />
            ),
            width: '150px'
        },
        // eslint-disable-next-line no-unused-vars
        ...optionalThead.filter(({ id }) => isColumnSelected(id)).map(({ id, ...rest }) => ({
            ...rest
        })),
        {
            title: (
                <ControlledMultiSelectionDropdown
                    key={ID_FOR_CONTROL_COLUMN_SELECTOR}
                    entityName=''
                    onApply={controlVM.handleChangeControlFilterColumns}
                    withIcon={<SelectLeftColumnIcon />}
                    onFetchItems={() => new Promise(resolve => resolve(controlVM.defaultColumns))}
                    value={controlVM.selectedFilterColumns}
                    defaultValue={controlVM.defaultColumns}
                    withoutHighlight
                    classNames='aligned-right'
                />
            ),
            width: '55px'
        },
        {
            title: (
                <div
                    className={classNames('search-input-container', {
                        'no-result': !controlVM.getMeetingsList().length && !!controlVM.searchQuery
                    })}
                >
                    <SearchInput
                        onChange={(e, value) => {
                            controlVM.setSearchQuery(value);
                        }}
                        withResetSearchButton
                        withDebounce
                        debounceTime={500}
                    />
                </div>
            ),
            width: '145px'
        }
    ];
    if (theads.length < 8) {
        theads.map((el, i) => {
            if (i < theads.length - 2)
                el.width = 'auto';
        });
    }

    const isLoading = controlVM.isLoading();
    const progressList = useMemo(() => getProgressList(controlVM.selectedFilterStatuses), [controlVM.selectedFilterStatuses]);
    const meetingsTimeGroups = getMeetingGroupsWithHiddenSection(controlVM.filteredMeetingsList, progressList);
    const event = controlVM.getEvent();

    const isEmpty = !controlVM.filteredMeetingsList.length && !isLoading;
    const isFilterActive =
        controlVM.selectedFilterTypes ||
        controlVM.selectedFilterDays.length ||
        controlVM.selectedFilterOperators ||
        controlVM.selectedFilterDayInEventTimezone ||
        controlVM.searchQuery;

    const getGroupRef = (group: IMeetingsGroup): RefObject<HTMLElement> | undefined => {
        return controlVM.filteredMeetingGroupRefs.find(el => el.startDateTime.toString() === group.startDateTime.toString())?.ref;
    };

    const onScrollHandler = () => {
        if (!isLoading && meetingsTimeGroups.length) {
            setCurrentScrollHourSection(controlVM.getCurrentScrollMeetingGroup().date);
            setIsScrollNextDisabled(window.scrollY + window.innerHeight + SCROLL_HEADER_HEIGHT > document.body.scrollHeight);
        }
    };

    useEffect(() => {
        controlVM.clearFilters();
        controlVM.fetchMeetingsRequest(!TRACK_FILTERS, LOAD_SAVED_FILTERS);

        return () => {
            controlVM.clearMeetings();
        };
    }, []);


    useEffect(() => {
        if (!isLoading && controlVM.filteredMeetingsList.length > 0) {
            setCurrentScrollHourSection(controlVM.filteredMeetingsList[0].startDateTime);
            // Use setTimeout to ensure DOM has updated after filtering
            setTimeout(() => {
                const isAtBottom = window.scrollY + window.innerHeight + SCROLL_HEADER_HEIGHT >= document.body.scrollHeight;
                setIsScrollNextDisabled(isAtBottom);
            }, 0);
        }
    }, [isLoading, controlVM.filteredMeetingsList.length]);

    useEffect(() => {
        window.addEventListener('scroll', onScrollHandler);
        return () => {
            window.removeEventListener('scroll', onScrollHandler);
        };
    }, [isLoading]);

    return (
        <section className='fixed-list-layout control'>
            <div>
                <ListLayout sortOptions={{ theads: theads }} isDisabled={isEmpty}>
                    {isLoading && <TableLoadingRow colSpan={theads.length} />}
                    {!isLoading && !!meetingsTimeGroups.length &&
                        <tr>
                            <th colSpan={theads.length} className='hour-navigation-row'>
                                <OENextPrevStepper
                                    datesArray={controlVM.filteredMeetingGroupRefs.map(ref => ref.startDateTime)}
                                    selectedHour={currentScrollHourSection}
                                    changeHour={controlVM.scrollToGroupElement}
                                    eventTimeZone={controlVM.getEvent().timeZone}
                                    isNowDisabled={!controlVM.getOngoingMeetingGroup()}
                                    isNextDisabled={isScrollNextDisabled}
                                    withNowButton
                                />
                            </th>
                        </tr>
                    }
                    {!isLoading && !!controlVM.filteredMeetingGroupRefs.length && meetingsTimeGroups.map((group: IMeetingsGroup) => (
                        <ControlListGroupItem
                            key={group.key}
                            group={group}
                            timeZone={event.timeZone}
                            colSpan={theads.length}
                            controlVM={controlVM}
                            groupHeaderRef={getGroupRef(group)}
                            showColumn={isColumnSelected}
                        />)
                    )}
                </ListLayout>
                { isEmpty && (
                    <div className='empty-list-message'>
                        {!isFilterActive ? (
                            <FormattedMessage
                                description='Meeting list empty text'
                                defaultMessage='There are no meetings scheduled in this event.'
                                id='oe.control.emptyListText'
                            />
                        ) : (
                            <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                        )}
                    </div>
                )}
            </div>
            {!isLoading && <Paging pagingVM={controlVM.getPaginationVM()} />}
        </section>
    );
});

export default injectIntl(ControlList);
